"use client";

import { useEffect, useMemo, useState } from "react";

import { usePlaybackState, useStudio } from "@/contexts/studio-context";
import { harmonyCursor, type HarmonyCursor, type HarmonyWindow } from "@/lib/harmony/cursor";
/** Whether the cursor moves freely or on the metronome. */
export type Regime = "free" | "timed";

/**
 * Le curseur harmonique, branché sur le transport.
 *
 * ## Il n'entre dans React que quand l'accord change
 *
 * Le curseur ne dit qu'une chose — quel accord est sous les mains — et elle ne
 * change qu'une fois par accord. La boucle de lecture, elle, tourne à l'image :
 * elle relit donc la position à chaque image et n'appelle `setState` que sur un
 * vrai changement de rang, sans quoi le bandeau serait re-rendu soixante fois
 * par seconde pour redessiner une phrase qui n'a pas bougé.
 *
 * Ce hook a porté un second canal, écrit directement dans le DOM : le
 * remplissage de la cellule courante du ruban, qui change à chaque image. Le
 * ruban servait le décor à couloir ouvert, celui des niveaux 5 à 9, et il est
 * parti avec lui. Voir `scene.ts`.
 *
 * ## La boucle se parke au repos
 *
 * Aucune `requestAnimationFrame` autonome ne tourne quand rien ne joue : le
 * produit a retiré exactement ce genre de boucle pour que le studio cesse de
 * chauffer à l'arrêt, et en réintroduire une ici l'annulerait. Elle démarre à
 * la lecture et s'arrête avec elle.
 */

export function useHarmonyCursor({
  chords,
  regime,
  index,
}: {
  /** Les accords de l'unité, dans l'ordre. */
  readonly chords: readonly HarmonyWindow[];
  readonly regime: Regime;
  /** Régime libre : l'accord sur lequel le joueur travaille. */
  readonly index: number;
}): HarmonyCursor {
  const { studio } = useStudio();
  const playing = usePlaybackState();

  const free = useMemo(
    () => harmonyCursor({ regime: "free", chords, index }),
    [chords, index],
  );

  const [timed, setTimed] = useState<HarmonyCursor>(free);

  useEffect(() => {
    if (regime !== "timed" || !studio) {
      return;
    }

    const transport = studio.getLocalTransport();
    let raf = 0;
    let last: HarmonyCursor | null = null;

    const read = () => {
      const next = harmonyCursor({
        regime: "timed",
        chords,
        tick: transport.currentTick(),
      });

      if (last === null || last.index !== next.index) {
        last = next;
        setTimed(next);
      }
    };

    read();

    if (!playing) {
      return;
    }

    const frame = () => {
      read();
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);

    return () => cancelAnimationFrame(raf);
  }, [regime, studio, playing, chords]);

  return regime === "free" ? free : timed;
}
